import { useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FormErrorSummary from '../../components/common/FormErrorSummary';
import clientiService from '../../services/clientiService';
import { getErrorMessage } from '../../utils/api';
import type { CreateClienteDto, UpdateClienteDto } from '../../types/cliente.types';

const ClienteFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!id;

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<CreateClienteDto | UpdateClienteDto>({
    defaultValues: {
      tipo: 'persona_fisica',
      nome: '',
      cognome: '',
      codiceFiscale: '',
      partitaIva: '',
      email: '',
      pec: '',
      telefono: '',
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
      note: '',
      personaGiuridica: false
    }
  });

  const { data: cliente, isLoading } = useQuery({
    queryKey: ['cliente', id],
    queryFn: () => clientiService.getById(id!),
    enabled: isEdit
  });

  const saveMutation = useMutation({
    mutationFn: (data: CreateClienteDto | UpdateClienteDto) =>
      isEdit
        ? clientiService.update(id!, data as UpdateClienteDto)
        : clientiService.create(data as CreateClienteDto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clienti'] });
      enqueueSnackbar(
        isEdit ? 'Cliente aggiornato con successo' : 'Cliente creato con successo',
        { variant: 'success' }
      );
      navigate('/clienti');
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il salvataggio'), { variant: 'error' });
    }
  });

  useEffect(() => {
    if (cliente) {
      reset(cliente);
    }
  }, [cliente, reset]);

  // Watch tipo cliente per mostrare campi dinamici
  const tipoCliente = watch('tipo');
  const isAzienda = tipoCliente === 'azienda';

  const onSubmit = (data: CreateClienteDto | UpdateClienteDto) => {
    saveMutation.mutate(data);
  };

  if (isEdit && isLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento cliente..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/clienti')} variant="outlined">
            Indietro
          </Button>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {isEdit ? 'Modifica Cliente' : 'Nuovo Cliente'}
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-label={isEdit ? 'Modifica cliente' : 'Crea nuovo cliente'}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormErrorSummary errors={errors} />

              {/* Tipo Cliente */}
              <Controller
                name="tipo"
                control={control}
                rules={{ required: 'Tipo cliente obbligatorio' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    label="Tipo Cliente"
                    required
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  >
                    <MenuItem value="persona_fisica">Persona Fisica</MenuItem>
                    <MenuItem value="azienda">Azienda</MenuItem>
                  </TextField>
                )}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Controller
                  name="cognome"
                  control={control}
                  rules={{ required: 'Cognome obbligatorio' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Cognome / Denominazione"
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="nome"
                  control={control}
                  rules={{ required: isAzienda ? false : 'Nome obbligatorio' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Nome"
                      required={!isAzienda}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || (isAzienda ? 'Opzionale per aziende' : '')}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Controller
                  name="codiceFiscale"
                  control={control}
                  rules={{
                    required: 'Codice fiscale obbligatorio',
                    validate: (value) => {
                      if (!value) return true;
                      const cf = value.toUpperCase().replace(/\s/g, '');
                      // Formato persona fisica: 16 caratteri alfanumerici
                      const cfPersonaFisica = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;
                      // Formato azienda: 11 cifre numeriche
                      const cfAzienda = /^\d{11}$/;
                      if (cfPersonaFisica.test(cf) || cfAzienda.test(cf)) {
                        return true;
                      }
                      return 'Formato non valido. Persona fisica: 16 caratteri. Azienda: 11 cifre';
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={isAzienda ? "Codice Fiscale Azienda" : "Codice Fiscale"}
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        (isAzienda
                          ? '11 cifre numeriche (es: 12345678901)'
                          : '16 caratteri alfanumerici (es: RSSMRA80A01H501U)')
                      }
                      inputProps={{
                        style: { textTransform: 'uppercase' },
                        maxLength: isAzienda ? 11 : 16
                      }}
                    />
                  )}
                />
                <Controller
                  name="partitaIva"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!value) return true;
                      const piva = value.replace(/\s/g, '');
                      if (/^\d{11}$/.test(piva)) {
                        return true;
                      }
                      return 'Formato non valido. Deve contenere 11 cifre';
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Partita IVA"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        '11 cifre numeriche (opzionale)'
                      }
                      inputProps={{
                        maxLength: 11
                      }}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: isAzienda ? 'Email obbligatoria per aziende' : false,
                    validate: (value) => {
                      // Valida formato solo se ha un valore
                      if (!value || value.trim() === '') return true;
                      const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
                      return emailPattern.test(value) || 'Formato email non valido';
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Email"
                      type="email"
                      required={isAzienda}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || (isAzienda ? 'Obbligatoria per aziende' : '')}
                    />
                  )}
                />
                <Controller
                  name="pec"
                  control={control}
                  rules={{
                    validate: (value) => {
                      // Campo opzionale: valida solo se ha un valore
                      if (!value || value.trim() === '') return true;
                      const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
                      return emailPattern.test(value) || 'Formato PEC non valido';
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="PEC"
                      type="email"
                      fullWidth
                      placeholder="mario@pec.esempio.it"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'Posta Elettronica Certificata (opzionale)'}
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Controller
                  name="telefono"
                  control={control}
                  render={({ field }) => <TextField {...field} label="Telefono" fullWidth />}
                />
                <Controller
                  name="indirizzo"
                  control={control}
                  render={({ field }) => <TextField {...field} label="Indirizzo" fullWidth />}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
                <Controller
                  name="citta"
                  control={control}
                  render={({ field }) => <TextField {...field} label="Citta" fullWidth />}
                />
                <Controller
                  name="cap"
                  control={control}
                  render={({ field }) => <TextField {...field} label="CAP" fullWidth />}
                />
                <Controller
                  name="provincia"
                  control={control}
                  render={({ field }) => <TextField {...field} label="Provincia" fullWidth />}
                />
              </Box>
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Note"
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Note aggiuntive..."
                  />
                )}
              />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/clienti')}
                  disabled={saveMutation.isPending}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saveMutation.isPending}
                >
                  {isEdit ? 'Aggiorna' : 'Crea'} Cliente
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default ClienteFormPage;
